import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { completeBooking, listBookings } from '../api/endpoints/bookings';
import { listSessions } from '../api/endpoints/sessions';
import type { Booking } from '../api/schemas';
import { useSession } from '../auth/useSession';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { AttendeeName, ClassLevelMix, RosterFileProvider } from '../components/RosterFile';
import { bookingsQueryKey, coachWeekWindow } from '../lib/bookingWindows';
import { buildCoachWeek, dayHeading, type CoachClass, type CoachDay } from '../lib/coachWeek';
import { formatMoneyCents, formatTime, isSameDay } from '../lib/format';
import { serviceName, staffName, useBusiness } from '../lib/useBusiness';

/** A coach checks people in by tapping; 30 s is enough to catch a late booking. */
const REFETCH_INTERVAL_MS = 30_000;

type StatusWord = { label: string; tone?: 'good' | 'off' };

const STATUS_WORD: Partial<Record<Booking['status'], StatusWord>> = {
  confirmed: { label: 'Reservó' },
  completed: { label: 'Llegó', tone: 'good' },
  paid: { label: 'Llegó', tone: 'good' },
  no_show: { label: 'No llegó', tone: 'off' },
};

/**
 * A coach's own week: the classes they teach and who holds a place in each.
 * The API narrows sessions and bookings to this coach, so nothing here filters.
 */
export function MisClasesPage() {
  const { session } = useSession();
  const business = useBusiness();
  const queryClient = useQueryClient();
  const range = useMemo(() => coachWeekWindow(new Date()), []);

  const sessionsQuery = useQuery({
    queryKey: ['coach-sessions', range.from],
    queryFn: () => listSessions(range.from, range.to),
    refetchInterval: REFETCH_INTERVAL_MS,
  });
  const bookingsQuery = useQuery({
    queryKey: bookingsQueryKey(range),
    queryFn: () => listBookings(range),
    refetchInterval: REFETCH_INTERVAL_MS,
  });
  const checkIn = useMutation({
    mutationFn: (booking: Booking) => completeBooking(booking.id, crypto.randomUUID()),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['business-bookings'] }),
  });

  const now = useMemo(() => new Date(), [bookingsQuery.dataUpdatedAt, sessionsQuery.dataUpdatedAt]);
  const week = useMemo(
    () =>
      sessionsQuery.data && bookingsQuery.data
        ? buildCoachWeek(sessionsQuery.data.data, bookingsQuery.data.data, now)
        : undefined,
    [sessionsQuery.data, bookingsQuery.data, now],
  );
  const name = staffName(business, session?.staffMemberId);
  const loadError = sessionsQuery.error ?? bookingsQuery.error;

  return (
    <RosterFileProvider>
      <main className="coach">
        <PageHeader
          title="Mis clases"
          description={name ? `${firstWord(name)}, estas son tus clases y quién viene.` : 'Tus clases y quién viene.'}
        />
        {sessionsQuery.isLoading || bookingsQuery.isLoading ? <LoadingState label="Cargando tus clases…" /> : null}
        {loadError ? <ErrorBanner message={messageFor(loadError)} /> : null}
        {checkIn.isError ? <ErrorBanner message={messageFor(checkIn.error)} /> : null}

        {week?.current ? (
          <CurrentClass
            entry={week.current}
            now={now}
            business={business}
            onCheckIn={(booking) => checkIn.mutate(booking)}
            checkingIn={checkIn.isPending ? checkIn.variables?.id : undefined}
          />
        ) : null}

        {week ? (
          <>
            <section className="section" aria-label="Lo que generaste">
              <h2 className="section-label">Generado en {monthName(now)}</h2>
              <div className="group coach-earned">
                <p className="coach-earned-amount">{formatMoneyCents(week.month.billedCents)}</p>
                <p className="row-sub">
                  Lo que se facturó en tus clases: {week.month.paidBookings}{' '}
                  {plural(week.month.paidBookings, 'reserva pagada', 'reservas pagadas')} en {week.month.visits}{' '}
                  {plural(week.month.visits, 'clase', 'clases')}.
                </p>
              </div>
            </section>

            <section className="section" aria-label="Tu semana">
              <h2 className="section-label">Tu semana</h2>
              <dl className="group coach-stats">
                <Figure label="Hoy" value={week.stats.today} unit={plural(week.stats.today, 'clase', 'clases')} />
                <Figure
                  label="Próximos 7 días"
                  value={week.stats.classes}
                  unit={plural(week.stats.classes, 'clase', 'clases')}
                />
                <Figure
                  label="Ocupación"
                  value={week.stats.fillPercent === null ? '—' : `${week.stats.fillPercent}%`}
                  unit={`${week.stats.people} ${plural(week.stats.people, 'persona', 'personas')}`}
                />
              </dl>
            </section>

            <section className="section" aria-label="Próximas clases">
              <h2 className="section-label">Próximas</h2>
              {week.upcoming.length === 0 ? (
                <EmptyState title="Sin clases por delante." description="Cuando te asignen una clase, aparece aquí." />
              ) : (
                week.upcoming.map((day, index) => (
                  <CoachDayBlock
                    key={day.key}
                    day={day}
                    now={now}
                    defaultOpen={index === 0}
                    business={business}
                    onCheckIn={(booking) => checkIn.mutate(booking)}
                    checkingIn={checkIn.isPending ? checkIn.variables?.id : undefined}
                  />
                ))
              )}
            </section>

            {week.past.length > 0 ? (
              <section className="section" aria-label="Clases pasadas">
                <details className="coach-past">
                  <summary>
                    <span className="section-label">Semana pasada</span>
                    <span className="row-sub">{pastSummary(week.past)}</span>
                    <Chevron />
                  </summary>
                  {week.past.map((day) => (
                    <CoachDayBlock key={day.key} day={day} now={now} defaultOpen={false} business={business} />
                  ))}
                </details>
              </section>
            ) : null}
          </>
        ) : null}
      </main>
    </RosterFileProvider>
  );
}

type Business = ReturnType<typeof useBusiness>;

/** The class running now (or the closest one ahead), then its attendance as its own list. */
function CurrentClass({
  entry,
  now,
  business,
  onCheckIn,
  checkingIn,
}: {
  entry: CoachClass;
  now: Date;
  business: Business;
  onCheckIn: (booking: Booking) => void;
  checkingIn?: string;
}) {
  const canCheckIn = isSameDay(new Date(entry.startsAt), now);
  return (
    <>
      <section className="section" aria-label={entry.isLive ? 'Clase en curso' : 'Tu próxima clase'}>
        <h2 className="section-label">{entry.isLive ? 'Ahora' : 'Tu próxima clase'}</h2>
        <div className="group coach-now" data-live={entry.isLive || undefined}>
          <p className="coach-now-when">
            {dayHeading(new Date(entry.startsAt), now)} · {formatTime(entry.startsAt)} a {formatTime(entry.endsAt)}
          </p>
          <p className="coach-now-title">{serviceName(business, entry.serviceId)}</p>
          <p className="row-sub">{timing(entry, now)}</p>
          <dl className="coach-now-figures">
            <div>
              <dt>Reservaron</dt>
              <dd>
                {entry.attendees.length} de {entry.capacity}
              </dd>
            </div>
            <div>
              <dt>Llegaron</dt>
              <dd>{entry.arrived}</dd>
            </div>
            <div>
              <dt>Cupos libres</dt>
              <dd>{Math.max(entry.capacity - entry.attendees.length, 0)}</dd>
            </div>
          </dl>
          <ClassLevelMix bookings={entry.attendees} />
        </div>
      </section>

      <section className="section" aria-label="Asistencia">
        <h2 className="section-label">Asistencia</h2>
        {entry.attendees.length === 0 ? (
          <p className="row-sub">Nadie ha reservado todavía.</p>
        ) : (
          <ul className="group coach-attendance" aria-label={`Asistencia de las ${formatTime(entry.startsAt)}`}>
            {entry.attendees.map((booking) => (
              <AttendeeRow
                key={booking.id}
                booking={booking}
                canCheckIn={canCheckIn}
                onCheckIn={onCheckIn}
                pending={checkingIn === booking.id}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function timing(entry: CoachClass, now: Date): string {
  const minutes = (iso: string) => Math.max(Math.round((Date.parse(iso) - now.getTime()) / 60_000), 0);
  if (entry.isLive) return `En curso, termina en ${minutes(entry.endsAt)} min`;
  const until = minutes(entry.startsAt);
  if (until < 60) return `Empieza en ${until} min`;
  const hours = Math.floor(until / 60);
  if (hours < 24) return `Empieza en ${hours} h`;
  const days = Math.round(hours / 24);
  return `Faltan ${days} ${plural(days, 'día', 'días')}`;
}

function CoachDayBlock({
  day,
  now,
  defaultOpen,
  openClassId,
  business,
  onCheckIn,
  checkingIn,
}: {
  day: CoachDay;
  now: Date;
  defaultOpen: boolean;
  openClassId?: string;
  business: Business;
  onCheckIn?: (booking: Booking) => void;
  checkingIn?: string;
}) {
  const people = day.classes.reduce((sum, entry) => sum + entry.attendees.length, 0);
  const billed = day.classes.reduce((sum, entry) => sum + entry.billedCents, 0);
  const classes = day.classes.filter((entry) => !entry.isCancelled).length;
  const isPastDay = day.classes.every((entry) => entry.isPast);
  return (
    <details className="coach-day" open={defaultOpen}>
      <summary>
        <span className="coach-day-title">{dayHeading(day.date, now)}</span>
        <span className="row-sub">
          {classes} {plural(classes, 'clase', 'clases')} ·{' '}
          {isPastDay ? `${formatMoneyCents(billed)} facturado` : `${people} ${plural(people, 'persona', 'personas')}`}
        </span>
        <Chevron />
      </summary>
      <div className="group rows coach-classes">
        {day.classes.map((entry) => (
          <CoachClassRow
            key={entry.id}
            entry={entry}
            business={business}
            defaultOpen={entry.id === openClassId}
            canCheckIn={Boolean(onCheckIn) && isSameDay(new Date(entry.startsAt), now)}
            onCheckIn={onCheckIn}
            checkingIn={checkingIn}
          />
        ))}
      </div>
    </details>
  );
}

function CoachClassRow({
  entry,
  business,
  defaultOpen,
  canCheckIn,
  onCheckIn,
  checkingIn,
}: {
  entry: CoachClass;
  business: Business;
  defaultOpen: boolean;
  canCheckIn: boolean;
  onCheckIn?: (booking: Booking) => void;
  checkingIn?: string;
}) {
  const minutes = Math.round((Date.parse(entry.endsAt) - Date.parse(entry.startsAt)) / 60_000);
  const fill = entry.capacity > 0 ? Math.min(entry.attendees.length / entry.capacity, 1) : 0;
  return (
    <details className="coach-class" open={defaultOpen} data-past={entry.isPast || undefined}>
      <summary>
        <span className="coach-time">{formatTime(entry.startsAt)}</span>
        <span className="grow">
          <span className="row-title">{serviceName(business, entry.serviceId)}</span>
          <span className="row-sub">
            {entry.isCancelled ? (
              <span className="status" data-tone="off">
                Cancelada
              </span>
            ) : (
              `${minutes} min · ${occupancy(entry)}`
            )}
          </span>
        </span>
        {entry.isCancelled ? null : entry.isPast ? (
          <span className="coach-amount">{formatMoneyCents(entry.billedCents)}</span>
        ) : (
          <span className="coach-meter" aria-hidden="true">
            <span style={{ transform: `scaleX(${fill})` }} />
          </span>
        )}
        <Chevron />
      </summary>
      {entry.attendees.length === 0 ? (
        <p className="row-sub coach-roster-empty">Nadie ha reservado todavía.</p>
      ) : (
        <>
          <div className="coach-roster-mix">
            <ClassLevelMix bookings={entry.attendees} />
          </div>
          <ul className="coach-roster" aria-label={`Quién viene a las ${formatTime(entry.startsAt)}`}>
            {entry.attendees.map((booking) => (
              <AttendeeRow
                key={booking.id}
                booking={booking}
                canCheckIn={canCheckIn}
                onCheckIn={onCheckIn}
                pending={checkingIn === booking.id}
              />
            ))}
          </ul>
        </>
      )}
    </details>
  );
}

function AttendeeRow({
  booking,
  canCheckIn,
  onCheckIn,
  pending,
}: {
  booking: Booking;
  canCheckIn: boolean;
  onCheckIn?: (booking: Booking) => void;
  pending: boolean;
}) {
  const word = STATUS_WORD[booking.status];
  const showCheckIn = canCheckIn && booking.status === 'confirmed' && onCheckIn;
  return (
    <li className="coach-attendee">
      <span className="coach-initial" aria-hidden="true">
        {(booking.customerName ?? '?').charAt(0)}
      </span>
      <AttendeeName booking={booking} />
      {showCheckIn ? (
        <button type="button" className="btn small secondary" disabled={pending} onClick={() => onCheckIn(booking)}>
          {pending ? 'Marcando…' : 'Llegó'}
        </button>
      ) : word ? (
        <span className="status" data-tone={word.tone}>
          {word.label}
        </span>
      ) : null}
    </li>
  );
}

function Figure({ label, value, unit }: { label: string; value: number | string; unit: string }) {
  return (
    <div className="coach-figure">
      <dt>{label}</dt>
      <dd>
        <span className="coach-figure-value">{value}</span>
        <span className="row-sub">{unit}</span>
      </dd>
    </div>
  );
}

function Chevron() {
  return (
    <svg className="coach-chevron" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M6 3.5 10.5 8 6 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function occupancy(entry: CoachClass): string {
  if (entry.isPast) return `${entry.arrived} de ${entry.attendees.length} llegaron`;
  return `${entry.attendees.length} de ${entry.capacity} cupos`;
}

function pastSummary(days: CoachDay[]): string {
  const classes = days.reduce((sum, day) => sum + day.classes.filter((entry) => !entry.isCancelled).length, 0);
  const billed = days.reduce((sum, day) => sum + day.classes.reduce((inner, entry) => inner + entry.billedCents, 0), 0);
  return `${classes} ${plural(classes, 'clase', 'clases')} · ${formatMoneyCents(billed)} facturado`;
}

function monthName(date: Date): string {
  return date.toLocaleDateString('es-PA', { month: 'long' });
}

/** Last week through the next eight days: inside the API's 31-day session window. */
function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

function firstWord(name: string): string {
  return name.split(' ')[0] ?? name;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar tus clases. Intenta de nuevo.';
}
